/**
 * **玩家设置** —— 昵称 / BGM 音量 / 音效音量 / UI 大小。
 *
 * 纯数据 + 纯函数：解析、规整、序列化都能在 node 里断言。
 * 真正落盘的地方在 `SettingsStore`（注入进来的，测试里换成内存实现）。
 *
 * ## 一条不能违反的规矩：**读取设置永远不许抛**
 *
 * 设置是**进游戏之前**读的东西。这里抛一次异常，玩家看到的就是白屏 ——
 * 而他连设置界面都进不去，没法自救。所以：
 *
 * - 任何字段缺失/类型不对/超范围 → **只回退那一个字段**，其余照常读
 * - 整段 JSON 坏掉 / 存储被禁用（隐私模式）/ 配额满 → 全部回默认值 + 存内存
 *
 * 手写解析而不是上 zod，就是因为这条：zod 的默认行为是"整份不合格就整份失败"，
 * 那正好是最不该发生的结果。项目的 zod 用在**协议**上（不合法就该拒绝），
 * 而设置是**本机状态**（不合法就修好它）。
 */

import { clampUiScale, DEFAULT_UI_SCALE } from '../render/ui/metrics';
import { LORE_SECTIONS, type LoreSection } from '../core/lore';
import type { Lang } from '../i18n';
import { parseGamepadLayout, type GamepadLayout } from '../render/input/gamepad';

/** 落盘用的键。带版本号 —— 将来改结构时可以并存读旧键做迁移 */
export const SETTINGS_STORAGE_KEY = 'ivory-roulette/settings';
export const SETTINGS_VERSION = 1;

/**
 * 昵称长度上限。
 *
 * ⚠️ 和 `keyboard.ts` 里 `TextBuffer` 的上限**必须一致** ——
 * 不一致的话就会出现"输得进去、存下来被截断"，玩家看到自己的名字莫名短了一截。
 */
export const NICKNAME_MAX = 16;

/**
 * 昵称允许的字符。**只收英文/数字/`_`/`-`**（评审决定）。
 *
 * 理由是躲开输入法：页面上不放任何可编辑元素，按键就不会被候选窗吞掉。
 * 详情见 `keyboard.ts` 的文件头 —— 那里记着第 6 步推翻"透明 input"方案的经过。
 * 代价也记在那儿：**中文昵称、剪贴板粘贴、光标选区都不支持**。
 */
export const NICKNAME_PATTERN = /^[A-Za-z0-9_-]$/;

export interface Settings {
  /** 玩家名。可能为空串 —— 空串时由 UI 决定怎么显示（通常回退到默认名） */
  readonly nickname: string;
  /** BGM 音量，0..1 */
  readonly bgmVolume: number;
  /** 音效音量，0..1 */
  readonly sfxVolume: number;
  /** UI 倍率，见 `metrics.ts` */
  readonly uiScale: number;
  /** 界面语言。首次运行跟随浏览器，之后以存下来的为准 */
  readonly lang: Lang;
  /**
   * 手柄布局。只影响“哪颗按键叫确认/返回”（标准映射下 A 的位置不同）。
   * 默认**任天堂**（用户拍板）。
   */
  readonly gamepadLayout: GamepadLayout;
  /**
   * 悬停卡牌时想显示哪些说明板块（`core/lore.ts` 的 `LORE_SECTIONS` 子集）。
   * 空数组 = 都不显示。默认全开。
   */
  readonly loreSections: readonly LoreSection[];
}

/** 悬停说明板块的默认集合：只开「功能」和「操作」。其余想细看的人在设置里打开。 */
export const DEFAULT_LORE_SECTIONS: readonly LoreSection[] = ['effect', 'hint'];

export const DEFAULT_SETTINGS: Settings = {
  nickname: '',
  bgmVolume: 0.6,
  sfxVolume: 0.8,
  uiScale: DEFAULT_UI_SCALE,
  lang: 'zh',
  gamepadLayout: 'nintendo',
  loreSections: DEFAULT_LORE_SECTIONS,
};

/**
 * 把 unknown 洗成一组合法的板块：认得的留下（按 `LORE_SECTIONS` 的 canonical 顺序去重）。
 * 不是数组（旧档 / 存坏了）→ 全开；空数组是**合法值**（玩家全关了）。
 */
function normalizeLoreSections(value: unknown): readonly LoreSection[] {
  if (!Array.isArray(value)) return DEFAULT_LORE_SECTIONS;
  const wanted = new Set(value.filter((v): v is string => typeof v === 'string'));
  return LORE_SECTIONS.filter((section) => wanted.has(section));
}

/** 把一个 unknown 洗成受支持的语言；不认识就是 null（由调用方给兜底值） */
export function parseLang(value: unknown): Lang | null {
  return value === 'zh' || value === 'en' ? value : null;
}

function clamp01(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** 把任意字符串洗成合法昵称：丢掉非法字符，按 `NICKNAME_MAX` 截断 */
export function normalizeNickname(raw: string): string {
  let out = '';
  for (const ch of raw) {
    if (!NICKNAME_PATTERN.test(ch)) continue;
    if (out.length >= NICKNAME_MAX) break;
    out += ch;
  }
  return out;
}

/** 这个字符能不能打进昵称/房间号 */
export function isNicknameChar(ch: string): boolean {
  return NICKNAME_PATTERN.test(ch);
}

/**
 * **这一趟会话的默认名种子。**
 *
 * 评审：「默认用户名 `Operator-0000` 改为 `Op-0x****`，其中星号是四个随机
 * 十六进制字符。这样避免大家忘了改名的时候全叫 0000。」
 *
 * 病根是**所有调用点都传 `0`** —— 于是"随机后缀"那套设施一直在，而每个人
 * 拿到的都是 `0000`。所以种子改成这里生成一次：
 *
 * - **一次页面加载生成一次**：同一个会话里标题页 / 汇合页 / 名册三处印的
 *   必须是同一个名字（每处各摇一次的话，页脚和名册会对不上）
 * - **不落盘**：它只是"还没起名字"时的显示回退值。刷新换一个名字是可以的，
 *   而"两台机器上的默认名互相撞车"才是要避免的那个
 *
 * ⚠️ 为什么不像 `core/` 那样禁用 `Math.random`：这里不是规则逻辑。`core/`
 * 的禁令是为了棋谱能重放，而"玩家的默认昵称"根本不进棋谱。
 */
const SESSION_NICKNAME_SEED = Math.floor(Math.random() * 0x1_0000);

/**
 * 没设昵称时用的默认名。形如 `Op-0x4f2a`。
 *
 * `seed` 显式给了就是**可复现**的（测试用）；不给就用这一趟会话的随机种子。
 */
export function defaultNickname(seed: number = SESSION_NICKNAME_SEED): string {
  const n = Math.abs(Math.trunc(seed)) % 0x10000;
  return `Op-0x${n.toString(16).padStart(4, '0')}`;
}

/** 显示用昵称：空串回退到默认名。`seed` 一般不用给，见 `defaultNickname` */
export function displayNickname(settings: Settings, seed?: number): string {
  return settings.nickname === '' ? defaultNickname(seed) : settings.nickname;
}

/**
 * 规整一份设置：**逐字段**回退，绝不整份失败。
 * `unknown` 入参是刻意的 —— 它的调用点就是"刚读完 localStorage / 刚 parse 完 JSON"。
 */
export function normalizeSettings(input: unknown, fallbackLang: Lang = 'zh'): Settings {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  return {
    nickname:
      typeof raw.nickname === 'string' ? normalizeNickname(raw.nickname) : DEFAULT_SETTINGS.nickname,
    bgmVolume: clamp01(raw.bgmVolume, DEFAULT_SETTINGS.bgmVolume),
    sfxVolume: clamp01(raw.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
    uiScale: clampUiScale(raw.uiScale),
    // 没存过 / 值不认识 → 用传入的兜底（main.ts 传浏览器语言）
    lang: parseLang(raw.lang) ?? fallbackLang,
    gamepadLayout: parseGamepadLayout(raw.gamepadLayout) ?? DEFAULT_SETTINGS.gamepadLayout,
    loreSections: normalizeLoreSections(raw.loreSections),
  };
}

/** 存盘用的文本。带 `v` 版本号，将来能迁移 */
export function serializeSettings(settings: Settings): string {
  return `${JSON.stringify({ v: SETTINGS_VERSION, ...settings }, null, 2)}\n`;
}

/**
 * 解析存盘的文本。**任何问题都只是回退，不抛。**
 * 传 `null`（没存过）也走这里，拿到默认值。
 */
export function parseSettings(text: string | null | undefined, fallbackLang: Lang = 'zh'): Settings {
  if (typeof text !== 'string' || text.trim() === '') {
    return { ...DEFAULT_SETTINGS, lang: fallbackLang };
  }
  try {
    return normalizeSettings(JSON.parse(text), fallbackLang);
  } catch {
    // 存盘内容坏了（手改过 / 写到一半断电）。回默认值，
    // 下一次 save() 就会把它覆盖成合法的
    return { ...DEFAULT_SETTINGS, lang: fallbackLang };
  }
}

// ============================================================
// 存储
// ============================================================

/** 只用到这两个方法。故意不依赖 `Storage` 类型，方便测试注入 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface SettingsStore {
  load(): Settings;
  save(settings: Settings): void;
}

/** 内存实现：测试用，也是 localStorage 不可用时的兜底 */
export function createMemoryStore(
  initial: Settings | null = null,
  fallbackLang: Lang = 'zh',
): SettingsStore {
  let current = initial === null ? null : serializeSettings(initial);
  return {
    load: () => parseSettings(current, fallbackLang),
    save: (settings) => {
      current = serializeSettings(settings);
    },
  };
}

/**
 * 包一层 `localStorage`。
 *
 * ⚠️ **每一次访问都可能在抛异常**：Safari 隐私模式下 `localStorage` 存在但
 * `setItem` 直接抛 `QuotaExceededError`；某些企业策略下连读都抛。
 * 所以这里两处都用 try/catch 兜住，失败就退化成"这次会话内有效"。
 * 玩家的设置存不下来是小事，**进不去游戏**是大事。
 */
export function createLocalStore(
  storage: StorageLike,
  key = SETTINGS_STORAGE_KEY,
  fallbackLang: Lang = 'zh',
): SettingsStore {
  let fallback: string | null = null;
  return {
    load(): Settings {
      try {
        return parseSettings(storage.getItem(key) ?? fallback, fallbackLang);
      } catch {
        return parseSettings(fallback, fallbackLang);
      }
    },
    save(settings: Settings): void {
      const text = serializeSettings(settings);
      fallback = text;
      try {
        storage.setItem(key, text);
      } catch {
        // 存不下就算了，fallback 已经拿到了这一份
      }
    },
  };
}

// ============================================================
// 控制器
// ============================================================

export type SettingsListener = (settings: Settings, previous: Settings) => void;

/** 两个板块列表是不是同一组（顺序也算，因为显示顺序就是它） */
function sameLoreSections(a: readonly LoreSection[], b: readonly LoreSection[]): boolean {
  return a.length === b.length && a.every((section, i) => section === b[i]);
}

/**
 * 当前的设置 + 变更通知。
 *
 * 刻意做成**不 import Pixi** 的普通对象：设置是"数据"，
 * 谁来画（Pixi 菜单、调试页的 DOM）都行。
 */
export interface SettingsController {
  get(): Settings;
  /** 改一部分。会自动规整，然后**只在真的变了**的时候存盘并通知 */
  patch(partial: Partial<Settings>): void;
  reset(): void;
  subscribe(listener: SettingsListener): () => void;
}

export function createSettingsController(
  store: SettingsStore,
  fallbackLang: Lang = 'zh',
): SettingsController {
  let current = store.load();
  const listeners = new Set<SettingsListener>();

  const publish = (next: Settings): void => {
    // 逐字段比较：`patch({uiScale: 1})` 在已经是 1 的时候不该触发重排整棵 UI
    const same =
      next.nickname === current.nickname &&
      next.bgmVolume === current.bgmVolume &&
      next.sfxVolume === current.sfxVolume &&
      next.uiScale === current.uiScale &&
      next.lang === current.lang &&
      next.gamepadLayout === current.gamepadLayout &&
      sameLoreSections(next.loreSections, current.loreSections);
    if (same) return;
    const previous = current;
    current = next;
    store.save(current);
    // 把"上一份"也传出去：订阅者往往只对**某几个字段变了**感兴趣
    // （比如宿主只在 uiScale 变了时才重建界面），自己存一份旧值很容易忘
    for (const listener of listeners) listener(current, previous);
  };

  return {
    get: () => current,
    patch(partial) {
      publish(normalizeSettings({ ...current, ...partial }));
    },
    reset() {
      publish({ ...DEFAULT_SETTINGS, lang: fallbackLang });
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
